import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { pushPageView } from '../../utils/gtm';

/**
 * GTM Route Tracker — null-render component placed once inside
 * <BrowserRouter>. Fires a deduped `page_view` data-layer event on the
 * initial render and on every client-side navigation.
 *
 * Why a component (not a top-level effect):
 *   `useLocation` must be consumed inside the Router context. Placing
 *   this component once at the root is the minimal, framework-idiomatic
 *   way to subscribe to route changes without touching any individual
 *   page or layout.
 */
const GtmRouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Defer one micro-tick so React Router has updated `document.title`
    // (in apps that set it via Helmet/effects) before we read it.
    // requestAnimationFrame is safe even when the page is hidden.
    let raf = null;
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      raf = window.requestAnimationFrame(() => {
        pushPageView({
          path: location.pathname + location.search,
          // title/url default to live document values inside pushPageView
        });
      });
    } else {
      pushPageView({ path: location.pathname + location.search });
    }
    return () => {
      if (raf && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(raf);
      }
    };
  }, [location.pathname, location.search]);

  return null;
};

export default GtmRouteTracker;
